import axios from 'axios';
import { Select } from 'ts/interfaces/Generic';
import { PackagesResponse, SearchDate, SelectGrouped } from 'ts/package/interfaces/Package';
import { SearchPackageModel } from 'ts/package/classes/SearchPackageModel';
import { GetPackageDetailsRequest } from 'ts/package/classes/GetPackageDetailsRequest';
import { PackageDetailsResponse } from 'ts/package/interfaces/PackageDetails';
import { BookPackageResponse, BookingModel } from 'ts/package/interfaces/BookPackageResponse';
import { SearchTourModel } from 'ts/package/classes/SearchTourModel';
import { DealModel, PackageOfflineResponse } from 'ts/package/interfaces/PackageOfflineResponse';
import { Screenshot } from 'utils/utils';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import { SubmitScreenshotsParams } from 'ts/shared/interfaces/Generic';
import { dateFormat } from 'utils/shared/utils';
import { ContactUsModel } from 'ts/package/classes/ContactUsModel';

interface GetDatesByCityParams {
  cityCode: string;
  type: number | string;
  recaptchaToken: string;
  flightSourcesDefault?: string;
}

interface GetDatesParams {
  cityCode: string;
  type: number;
  checkIn: string;
  recaptchaToken: string;
}

interface SearchTypes {
  roomTypes: Select[];
  types: Select[];
}

export const getTypes = async (): Promise<SearchTypes> => {
  const lang = localStorage.getItem('language') || 'he';
  const types = sessionStorage.getItem('ldsTypes' + lang);
  if (types) {
    return JSON.parse(types);
  }
  try {
    const url = 'ILPackages/GetSearchFilters';
    const data = (await axios.post(url, undefined, { params: { lang } })).data;

    const newTypes = {
      types: data.typesList.map((type: any) => {
        return {
          value: type.value,
          label: type.text,
        };
      }),
      roomTypes: data.roomTypes.map((type: any) => {
        return {
          value: type.value,
          label: type.text,
        };
      }),
    };
    sessionStorage.setItem('ldsTypes' + lang, JSON.stringify(newTypes));
    return newTypes;
  } catch (err) {
    return {
      types: [],
      roomTypes: [],
    };
  }
};

export const getCities = async (ldsType: string): Promise<SelectGrouped[] | undefined> => {
  try {
    const defaultUrl = 'ILPackages/GetCitiesDefault';
    const popularUrl = 'ILPackages/GetCitiesPopular';
    const localLeng = localStorage.getItem('language');
    const lang = localLeng === 'he' || !localLeng ? 'He' : 'En';

    const defaultCities = (await axios.post(defaultUrl, null, { params: { ldsType, lang } })).data;
    const popularCities = (await axios.post(popularUrl, null, { params: { ldsType, lang } })).data;

    const formatCities = (cities: any, index: number) => {
      return cities.citiesLists.map((city: any) => {
        return {
          value: city.value + '-' + index,
          label: city.text,
        };
      });
    };

    return [
      { label: 'package.popular', options: formatCities(popularCities, 0) },
      { label: 'package.alphabetically', options: formatCities(defaultCities, 1) },
    ];
  } catch (err) {
    return undefined;
  }
};

export const getDatesByCity = async (
  params: GetDatesByCityParams
): Promise<SearchDate[] | undefined> => {
  try {
    const url = 'ILPackages/GetDatesByCity';
    return (await axios.post(url, params)).data.dates
      .map((response: any) => {
        return {
          date: new Date(response.date),
          minPrice: response.minPrice,
        };
      })
      .sort((a: SearchDate, b: SearchDate) => a.date.getTime() - b.date.getTime());
  } catch (err) {
    return undefined;
  }
};

export const getDates = async (params: GetDatesParams): Promise<SearchDate[] | undefined> => {
  try {
    const url = 'ILPackages/GetDates';
    return (await axios.post(url, { ...params, isBackDates: true })).data.date.returnDates
      .map((response: any) => {
        return {
          date: new Date(response.date),
          minPrice: response.minPrice,
        };
      })
      .sort((a: SearchDate, b: SearchDate) => a.date.getTime() - b.date.getTime());
  } catch (err) {
    return undefined;
  }
};
export const getTourDates = async (
  cityCode: string,
  flightSourcesDefault: string,
  recaptchaToken: string
): Promise<SearchDate[] | undefined> => {
  try {
    const url = 'ILPackages/GetTourDates';
    const body = { cityCode, flightSourcesDefault, type: 1, recaptchaToken };
    return (await axios.post(url, body)).data.tourDates
      .map((response: any) => {
        return {
          date: new Date(response.date),
          minPrice: response.minPrice,
        };
      })
      .sort((a: SearchDate, b: SearchDate) => a.date.getTime() - b.date.getTime());
  } catch (err) {
    return undefined;
  }
};

export const getPackages = async (
  params: SearchPackageModel
): Promise<PackagesResponse | undefined> => {
  try {
    const url = 'ILPackages/Search';
    const body = {
      ...params,
      checkIn: dateFormat(params.checkIn, 'yyyy-MM-dd', 'en'),
      cityCode: params.cityCode.split('-')[0],
      type: parseInt(params.type),
      lang: localStorage.getItem('language') || 'he',
    };
    const response = (await axios.post<PackagesResponse>(url, body)).data;
    sessionStorage.setItem('searchPackagesToken', response.searchToken);
    const deals = response.deals.map((deal) => {
      const result = {
        ...deal,
        visible: true,
      };
      if (response.additionalInfo[deal.uniqueKey]) {
        result.departureDate = response.additionalInfo[deal.uniqueKey].departDate;
        result.returnDate = response.additionalInfo[deal.uniqueKey].returnDate;
        result.boardingDescription = response.additionalInfo[deal.uniqueKey].boardingDescription;
        result.cityFullDescription = response.additionalInfo[deal.uniqueKey].cityFullDescription;
      }
      return result;
    });
    return {
      ...response,
      deals,
    };
  } catch (err) {
    return undefined;
  }
};

export const getTours = async (
  params: SearchTourModel,
  recaptchaToken: string
): Promise<PackagesResponse | undefined> => {
  try {
    const url = 'ILPackages/Search';
    const body = {
      checkIn: dateFormat(params.checkIn, 'yyyy-MM-dd', 'en'),
      cityCode: params.cityCode.split('-')[0],
      type: 1,
      roomType: params.roomType,
      recaptchaToken,
      flightSourcesDefault: params.flightSourcesDefault,
    };
    const response = (await axios.post<PackagesResponse>(url, body)).data;
    sessionStorage.setItem('searchTourToken', response.searchToken);
    const deals = response.deals.map((deal) => {
      const result = {
        ...deal,
        visible: true,
      };

      if (response.additionalInfo[deal.uniqueKey]) {
        result.departureDate = response.additionalInfo[deal.uniqueKey].departDate;
        result.returnDate = response.additionalInfo[deal.uniqueKey].returnDate;
        result.boardingDescription = response.additionalInfo[deal.uniqueKey].boardingDescription;
        result.cityFullDescription = response.additionalInfo[deal.uniqueKey].cityFullDescription;
      }

      return result;
    });
    return {
      ...response,
      deals,
    };
  } catch (err) {
    return undefined;
  }
};

export const getPackageDetails = async (
  body: GetPackageDetailsRequest
): Promise<PackageDetailsResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'ILPackages/Details';
    return (await axios.post<PackageDetailsResponse>(url, { ...body, language })).data;
  } catch (err) {
    return undefined;
  }
};

export const getBookDetails = async (
  body: GetPackageDetailsRequest
): Promise<BookPackageResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'ILPackages/Book';
    return (await axios.post<BookPackageResponse>(url, { ...body, language })).data;
  } catch (err) {
    return undefined;
  }
};

export const sendRequest = async (body: BookingModel): Promise<BookPackageResponse | undefined> => {
  try {
    const screenshotsJson = sessionStorage.getItem('screenshotsPackages');
    let screenshots: string[] = [];
    if (screenshotsJson) {
      const screenshotsArr: Screenshot[] = JSON.parse(screenshotsJson);
      screenshots = screenshotsArr.map((screenShots) => screenShots.data);
    }
    const language = 1;
    const url = 'ILPackages/BookComplete';
    return (await axios.post<BookPackageResponse>(url, { ...body, language, screenshots })).data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatus = async (transactionId: string): Promise<any | undefined> => {
  try {
    const url = 'ILPackages/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};
export const sendOfflineRequest = async (
  body: DealModel
): Promise<BookPackageResponse | undefined> => {
  try {
    const screenshotsJson = sessionStorage.getItem('screenshotsPackages');
    let screenshots: string[] = [];
    if (screenshotsJson) {
      const screenshotsArr: Screenshot[] = JSON.parse(screenshotsJson);
      screenshots = screenshotsArr.map((screenShots) => screenShots.data);
    }
    const url = 'OfflinePackages/BookComplete';
    const owner = {
      firstName: body.paxes[0].firstName,
      lastName: body.paxes[0].lastName,
      email: body.paxes[0].email,
      phone: body.paxes[0].phone,
    };
    return (
      await axios.post<BookPackageResponse>(url, { ...body, owner, language: 1, screenshots })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatusOffline = async (
  transactionId: string
): Promise<any | undefined> => {
  try {
    const url = 'OfflinePackages/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};

export const getOfflinePackage = async (
  name: string
): Promise<PackageOfflineResponse | undefined> => {
  try {
    const url = 'OfflinePackages/Details';
    return (await axios.post(url, { name })).data;
  } catch (err) {
    return undefined;
  }
};

export const getBookOfflinePackage = async (body: {
  name: string;
  priceUkey: string;
  searchToken: string;
}): Promise<PackageOfflineResponse | undefined> => {
  try {
    const url = 'OfflinePackages/Book';
    return (await axios.post(url, { ...body })).data;
  } catch (err) {
    return undefined;
  }
};

export const submitScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'ILPackages/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const submitOfflineScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'OfflinePackages/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken?: string;
  lang: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};

export interface SpecialTermsData {
  code: string;
  sysCode: number;
  descLang: string;
  desc: string;
}
export const getSpecialTerms = async (
  params: GetPackageDetailsRequest
): Promise<SpecialTermsData[] | undefined> => {
  try {
    const url = 'IlPackages/Terms';
    return (await axios.post(url, params)).data.listMessages;
  } catch (err) {
    return undefined;
  }
};

interface AnyPageParams {
  products: string;
  category: string;
  systemProductsDetails: string;
}

export const getAnyPage = async (params: AnyPageParams): Promise<CmsPageResponse | undefined> => {
  try {
    const url = 'data/AnyPage';
    return (await axios.post<CmsPageResponse>(url, { ...params, language: 'he' })).data;
  } catch (err) {
    return undefined;
  }
};

export const sendContactUs = async (data: ContactUsModel): Promise<any | undefined> => {
  try {
    const url = 'Utils/ContactUs';
    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};
